/**
 * WorkViewToggle Component
 *
 * A toggle button group for switching between "My Work", "Team", and "All" views.
 * Used in the sidebar or top bar to control data filtering across the app.
 */

import { useWorkView } from '../hooks/useWorkView'
import type { WorkViewMode } from '../types/agencyTeam'
import styles from './WorkViewToggle.module.css'

type WorkViewToggleProps = {
  /** Compact mode for sidebar */
  compact?: boolean
  /** Additional class name */
  className?: string
}

export default function WorkViewToggle({ compact = false, className = '' }: WorkViewToggleProps) {
  const {
    viewMode,
    setViewMode,
    availableViewModes,
    viewModeLabels,
    isAdmin,
  } = useWorkView()

  // Icons for each view mode
  const viewModeIcons: Record<WorkViewMode, JSX.Element> = {
    'my-work': (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    ),
    'team': (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    'all': (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <line x1="3" y1="9" x2="21" y2="9" />
        <line x1="9" y1="21" x2="9" y2="9" />
      </svg>
    ),
  }

  return (
    <div className={`${styles.container} ${compact ? styles.compact : ''} ${className}`}>
      <div className={styles.toggleGroup}>
        {availableViewModes.map((mode) => (
          <button
            key={mode}
            onClick={() => setViewMode(mode)}
            className={`${styles.toggleButton} ${viewMode === mode ? styles.active : ''}`}
            title={viewModeLabels[mode]}
          >
            <span className={styles.icon}>{viewModeIcons[mode]}</span>
            {!compact && <span className={styles.label}>{viewModeLabels[mode]}</span>}
          </button>
        ))}
      </div>

      {/* Badge showing context */}
      {!compact && (
        <div className={styles.context}>
          {isAdmin && <span className={styles.badge}>Admin</span>}
        </div>
      )}
    </div>
  )
}

/**
 * Inline version for use in page headers
 */
export function WorkViewToggleInline({ className = '' }: { className?: string }) {
  const { viewMode, setViewMode, availableViewModes, viewModeLabels } = useWorkView()

  // One mode is no choice; a lone "Mine" button would only invite a click
  // that does nothing.
  if (availableViewModes.length < 2) return null

  return (
    <div className={`${styles.inlineContainer} ${className}`}>
      {availableViewModes.map((mode) => (
        <button
          key={mode}
          onClick={() => setViewMode(mode)}
          className={`${styles.inlineButton} ${viewMode === mode ? styles.inlineActive : ''}`}
        >
          {viewModeLabels[mode]}
        </button>
      ))}
    </div>
  )
}
